import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { KeyRound, Trash2, UserPlus } from 'lucide-react';
import {
  useBulkUpdateProfilesMutation,
  useCreateUserMutation,
  useDeleteUserMutation,
  useGetProfilesQuery,
  useResetUserPasswordMutation,
  useUpdateProfileMutation,
} from '../../store/slices/adminSlice';
import type { Profile } from '../../store/slices/adminSlice';
import { useAuth } from '../../contexts/AuthContext';
import { useGetEmployeesQuery } from '../../store/slices/employeeSlice';
import type { Employee } from '../../store/slices/employeeSlice';
import type { AuthRole } from '../../utils/roles';
import {
  ErrorState,
  FilterChip,
  KpiGrid,
  KpiTile,
  PersonCell,
  SearchField,
  StatusPill,
  TableCard,
  TableEmptyRow,
  TableSkeletonRows,
  Toolbar,
} from '../../components/ui';
import { sbg } from '../../theme/sbgTokens';
import { MISSING, formatCount, formatDateTime, formatNumber, formatPercent, formatRelative } from '../../utils/format';
import { ROLE_OPTIONS, ROLE_SUMMARY, roleLabel } from './settings-parts/roles';
import {
  DeleteUserDialog,
  ResetPasswordDialog,
  RoleChangeDialog,
  avatarBasis,
  displayName,
} from './settings-parts/AccessDialogs';
import type { RoleChangeRequest } from './settings-parts/AccessDialogs';
import CreateUserSheet, { EMPTY_CREATE_FORM } from './settings-parts/CreateUserSheet';
import type { CreateUserForm } from './settings-parts/CreateUserSheet';
import LinkedEmployeePicker, {
  OptionText,
  quietFieldSx,
  toEmployeeOptions,
} from './settings-parts/LinkedEmployeePicker';
import type { EmployeeOption } from './settings-parts/LinkedEmployeePicker';

const THIRTY_DAYS_MS = 1000 * 60 * 60 * 24 * 30;
const COLUMN_COUNT = 6;
/** Success and info toasts close on their own after 5 seconds; errors stay until dismissed (book §11.13). */
const NOTICE_AUTO_HIDE_MS = 5000;
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

type SortKey = 'name' | 'role' | 'lastSignIn';
type SortState = { key: SortKey; direction: 'asc' | 'desc' };
type NoticeSeverity = 'success' | 'info' | 'error';
type Notice = { id: number; severity: NoticeSeverity; text: string };

const signInTime = (profile: Profile) => {
  const time = profile.lastSignInAt ? new Date(profile.lastSignInAt).getTime() : NaN;
  return Number.isFinite(time) ? time : null;
};

const sortName = (profile: Profile) => (profile.fullName || profile.email || '').toLowerCase();

const compareProfiles = (a: Profile, b: Profile, sort: SortState) => {
  let result = 0;
  if (sort.key === 'role') {
    result = ROLE_OPTIONS.indexOf(a.role ?? 'employee') - ROLE_OPTIONS.indexOf(b.role ?? 'employee');
  } else if (sort.key === 'lastSignIn') {
    result = (signInTime(a) ?? 0) - (signInTime(b) ?? 0);
  }
  if (result === 0) result = sortName(a).localeCompare(sortName(b));
  return sort.direction === 'asc' ? result : -result;
};

const apiErrorText = (error: unknown, fallback: string) => {
  const response = error as { data?: { error?: unknown } };
  return typeof response?.data?.error === 'string' ? response.data.error : fallback;
};

const placeholderText = (text: string) => (
  <Box component="span" sx={{ color: sbg.text.secondary }}>
    {text}
  </Box>
);

const roleMenuItems = ROLE_OPTIONS.map((role) => (
  <MenuItem key={role} value={role}>
    <OptionText primary={roleLabel(role)} secondary={ROLE_SUMMARY[role]} />
  </MenuItem>
));

const UserAccess: React.FC = () => {
  const { user, actualRole } = useAuth();
  const canDeleteUsers = actualRole === 'admin';
  const { data: profiles = [], isLoading, error, refetch } = useGetProfilesQuery();
  const { data: employees = [] } = useGetEmployeesQuery();
  const [updateProfile] = useUpdateProfileMutation();
  const [bulkUpdateProfiles, { isLoading: isBulkUpdating }] = useBulkUpdateProfilesMutation();
  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
  const [resetUserPassword, { isLoading: isResetting }] = useResetUserPasswordMutation();
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();

  const [notice, setNotice] = useState<Notice | null>(null);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const noticeCount = useRef(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<Set<AuthRole>>(new Set());
  const [sort, setSort] = useState<SortState>({ key: 'name', direction: 'asc' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[1]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkRole, setBulkRole] = useState<AuthRole | ''>('');
  const [linkingId, setLinkingId] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateUserForm>(EMPTY_CREATE_FORM);
  const [createError, setCreateError] = useState('');

  const [roleChange, setRoleChange] = useState<RoleChangeRequest | null>(null);
  const [roleChangeError, setRoleChangeError] = useState('');
  const [roleChangePending, setRoleChangePending] = useState(false);

  const [resetTarget, setResetTarget] = useState<Profile | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetError, setResetError] = useState('');

  const [userToDelete, setUserToDelete] = useState<Profile | null>(null);
  const [deleteError, setDeleteError] = useState('');

  const employeeById = useMemo(() => new Map<string, Employee>(employees.map((employee) => [employee._id, employee])), [employees]);
  const employeeOptions = useMemo(() => toEmployeeOptions(employees), [employees]);
  const employeeOptionById = useMemo(
    () => new Map<string, EmployeeOption>(employeeOptions.filter((option) => option.id).map((option) => [option.id, option])),
    [employeeOptions]
  );

  /** One toast at a time (book §11.13); a new notice replaces the last and restarts its timer. */
  const showNotice = (severity: NoticeSeverity, text: string) => {
    noticeCount.current += 1;
    setNotice({ id: noticeCount.current, severity, text });
    setNoticeOpen(true);
  };

  const roleCounts = useMemo(() => {
    const counts = Object.fromEntries(ROLE_OPTIONS.map((role) => [role, 0])) as Record<AuthRole, number>;
    profiles.forEach((profile) => {
      counts[profile.role ?? 'employee'] += 1;
    });
    return counts;
  }, [profiles]);

  const filteredProfiles = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return profiles.filter((profile) => {
      if (roleFilter.size > 0 && !roleFilter.has(profile.role ?? 'employee')) return false;
      if (!query) return true;
      return [profile.email, profile.fullName, profile.role, profile.role && roleLabel(profile.role), profile.employeeName, profile.departmentName]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [profiles, searchTerm, roleFilter]);

  const visibleIds = useMemo(() => new Set(filteredProfiles.map((profile) => profile.id)), [filteredProfiles]);
  const sortedProfiles = useMemo(() => [...filteredProfiles].sort((a, b) => compareProfiles(a, b, sort)), [filteredProfiles, sort]);
  const pageCount = Math.max(1, Math.ceil(sortedProfiles.length / rowsPerPage));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = sortedProfiles.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);

  const stats = useMemo(() => {
    const now = Date.now();
    return {
      linked: profiles.filter((profile) => profile.employeeId).length,
      admins: profiles.filter((profile) => profile.role === 'admin').length,
      recent: profiles.filter((profile) => {
        const time = signInTime(profile);
        return time !== null && now - time <= THIRTY_DAYS_MS;
      }).length,
    };
  }, [profiles]);

  const clearFilters = () => {
    setSearchTerm('');
    setRoleFilter(new Set());
    setPage(0);
  };

  const toggleRoleFilter = (role: AuthRole) => {
    setRoleFilter((previous) => {
      const next = new Set(previous);
      if (next.has(role)) next.delete(role);
      else next.add(role);
      return next;
    });
    setPage(0);
  };

  const toggleSort = (key: SortKey) => {
    setSort((previous) =>
      previous.key === key
        ? { key, direction: previous.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'lastSignIn' ? 'desc' : 'asc' }
    );
  };

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const pageSelectedCount = pageRows.filter((profile) => selectedIds.has(profile.id)).length;
  const togglePageSelection = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const selectAll = pageSelectedCount < pageRows.length;
      pageRows.forEach((profile) => (selectAll ? next.add(profile.id) : next.delete(profile.id)));
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setBulkRole('');
  };

  /* Role changes ------------------------------------------------------- */

  const requestRoleChange = (profile: Profile, role: AuthRole) => {
    if ((profile.role ?? 'employee') === role) return;
    setRoleChangeError('');
    setRoleChange({ profiles: [profile], role });
  };

  const requestBulkRoleChange = () => {
    if (!bulkRole || selectedIds.size === 0) return;
    // Only accounts that actually change are confirmed and sent; the rest are counted, not rewritten.
    const selectedProfiles = profiles.filter((profile) => selectedIds.has(profile.id));
    const changing = selectedProfiles.filter((profile) => (profile.role ?? 'employee') !== bulkRole);
    const unchanged = selectedProfiles.length - changing.length;
    if (changing.length === 0) {
      showNotice(
        'info',
        `${selectedProfiles.length === 1 ? 'The selected user is' : 'All selected users are'} already ${roleLabel(bulkRole)}. Nothing changed.`
      );
      return;
    }
    setRoleChangeError('');
    setRoleChange({
      profiles: changing,
      role: bulkRole,
      bulk: { ids: changing.map((profile) => profile.id), unchanged },
    });
  };

  const handleRoleChange = async () => {
    if (!roleChange || roleChangePending) return;
    const { role, bulk } = roleChange;
    setRoleChangePending(true);
    setRoleChangeError('');
    try {
      if (bulk) {
        const updates = bulk.ids.map((id) => ({ id, role }));
        await bulkUpdateProfiles({ updates }).unwrap();
        setSelectedIds(new Set());
        setBulkRole('');
        showNotice('success', `Changed ${formatCount(updates.length, 'user')} to ${roleLabel(role)}.`);
      } else {
        const [profile] = roleChange.profiles;
        await updateProfile({ id: profile.id, updates: { role } }).unwrap();
        showNotice('success', `${displayName(profile)} is now ${roleLabel(role)}.`);
      }
      setRoleChange(null);
    } catch (changeError) {
      setRoleChangeError(apiErrorText(changeError, 'Couldn’t change the role. Nothing was saved; try again in a minute.'));
    } finally {
      setRoleChangePending(false);
    }
  };

  /* Employee link ------------------------------------------------------ */

  const handleEmployeeLink = async (profile: Profile, employeeId: string) => {
    const payload = employeeId ? employeeId : null;
    setLinkingId(profile.id);
    try {
      await updateProfile({ id: profile.id, updates: { employeeId: payload } }).unwrap();
      const employee = payload ? employeeById.get(payload) : null;
      showNotice(
        'success',
        employee ? `Linked ${displayName(profile)} to ${employee.name}.` : `Removed the employee link for ${displayName(profile)}.`
      );
    } catch (linkError) {
      showNotice(
        'error',
        apiErrorText(linkError, `Couldn’t update the employee link for ${displayName(profile)}. Try again in a minute.`)
      );
    } finally {
      setLinkingId(null);
    }
  };

  /* Create ------------------------------------------------------------- */

  const openCreate = () => {
    setCreateError('');
    setCreateOpen(true);
  };

  const handleCreateUser = async () => {
    const payload = {
      email: createForm.email.trim(),
      fullName: createForm.fullName.trim() || null,
      role: createForm.role,
      employeeId: createForm.employeeId || null,
      mode: createForm.mode,
      temporaryPassword: createForm.mode === 'password' ? createForm.temporaryPassword : undefined,
    };
    setCreateError('');
    try {
      await createUser(payload).unwrap();
      setCreateForm(EMPTY_CREATE_FORM);
      setCreateOpen(false);
      showNotice(
        'success',
        payload.mode === 'invite'
          ? `Invite sent to ${payload.email}.`
          : `Account created for ${payload.email}. Share the temporary password with them directly.`
      );
    } catch (createFailure) {
      setCreateError(apiErrorText(createFailure, 'Couldn’t create this account. Your entries are still here; try again in a minute.'));
    }
  };

  /* Reset password ----------------------------------------------------- */

  const openResetDialog = (profile: Profile) => {
    setResetTarget(profile);
    setResetPassword('');
    setResetError('');
  };

  const closeResetDialog = () => {
    if (isResetting) return;
    setResetTarget(null);
    setResetPassword('');
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    setResetError('');
    try {
      await resetUserPassword({ id: resetTarget.id, temporaryPassword: resetPassword }).unwrap();
      showNotice('success', `Temporary password set for ${resetTarget.email}. Share it with them directly.`);
      setResetTarget(null);
      setResetPassword('');
    } catch (resetFailure) {
      setResetError(apiErrorText(resetFailure, 'Couldn’t reset the password. Try again in a minute.'));
    }
  };

  /* Delete ------------------------------------------------------------- */

  const openDeleteDialog = (profile: Profile) => {
    if (!canDeleteUsers || !user || profile.id === user.id) return;
    setDeleteError('');
    setNoticeOpen(false);
    setUserToDelete(profile);
  };

  const closeDeleteDialog = () => {
    if (isDeleting) return;
    setUserToDelete(null);
    setDeleteError('');
  };

  const handleDeleteUser = async () => {
    if (!userToDelete || !canDeleteUsers || !user || userToDelete.id === user.id || isDeleting) return;
    setDeleteError('');
    try {
      await deleteUser(userToDelete.id).unwrap();
      setSelectedIds((previous) => {
        const next = new Set(previous);
        next.delete(userToDelete.id);
        return next;
      });
      showNotice('success', `User account for ${userToDelete.email} deleted. Employee records and history were kept.`);
      setUserToDelete(null);
    } catch (deleteFailure) {
      setDeleteError(apiErrorText(deleteFailure, 'Couldn’t delete this account. Try again in a minute.'));
    }
  };

  /* Render ------------------------------------------------------------- */

  const kpiValue = (value: number) => (error ? MISSING : formatNumber(value));
  const total = profiles.length;

  const sortableHeader = (key: SortKey, label: string, align: 'left' | 'right' = 'left') => (
    <TableCell align={align} sortDirection={sort.key === key ? sort.direction : false}>
      <TableSortLabel
        active={sort.key === key}
        direction={sort.key === key ? sort.direction : key === 'lastSignIn' ? 'desc' : 'asc'}
        onClick={() => toggleSort(key)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const filterSummary = [
    searchTerm.trim() ? `“${searchTerm.trim()}”` : null,
    roleFilter.size ? Array.from(roleFilter).map(roleLabel).join(', ') : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const toolbar = (
    <Toolbar
      end={
        <Button variant="contained" startIcon={<UserPlus />} onClick={openCreate}>
          Add user
        </Button>
      }
    >
      <SearchField
        label="Search users"
        placeholder="Search name, email, role or employee"
        value={searchTerm}
        onChange={(value) => {
          setSearchTerm(value);
          setPage(0);
        }}
        width={300}
      />
      <Box role="group" aria-label="Filter by role" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {ROLE_OPTIONS.map((role) => (
          <FilterChip
            key={role}
            selected={roleFilter.has(role)}
            onClick={() => toggleRoleFilter(role)}
            count={isLoading || error ? undefined : roleCounts[role]}
          >
            {roleLabel(role)}
          </FilterChip>
        ))}
      </Box>
    </Toolbar>
  );

  // Selected people the current search or role filters hide: the selection keeps them, so say so.
  const hiddenSelectedCount = selectedIds.size > 0 ? Array.from(selectedIds).filter((id) => !visibleIds.has(id)).length : 0;

  // Bulk actions sit in their own row under the toolbar, so search and filters stay usable while
  // people are selected (search, tick, search again, then change every selected role at once).
  const selectionRow = selectedIds.size > 0 && (
    <Box
      role="group"
      aria-label="Selected users"
      sx={{
        mx: -2,
        mb: -1.5,
        px: 2,
        py: 1,
        bgcolor: sbg.bg.selected,
        borderTop: `1px solid ${sbg.border.subtle}`,
      }}
    >
      <Toolbar
        end={
          <Button variant="text" size="small" onClick={clearSelection}>
            Clear selection
          </Button>
        }
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap', mr: 1 }}>
          <Typography variant="labelMd" component="p" sx={{ color: sbg.text.primary }}>
            {formatCount(selectedIds.size, 'user')} selected
          </Typography>
          {hiddenSelectedCount > 0 && (
            <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary }}>
              {formatNumber(hiddenSelectedCount)} hidden by filters
            </Typography>
          )}
        </Box>
        <Select
          size="small"
          displayEmpty
          value={bulkRole}
          onChange={(event) => setBulkRole(event.target.value as AuthRole | '')}
          inputProps={{ 'aria-label': 'New role for selected users' }}
          renderValue={(value) => (value ? roleLabel(value as AuthRole) : placeholderText('Choose a role'))}
          sx={{ minWidth: 168, bgcolor: sbg.bg.surface }}
        >
          {roleMenuItems}
        </Select>
        <Tooltip title={bulkRole ? '' : 'Choose a role first'}>
          <span>
            <Button variant="contained" size="small" disabled={!bulkRole || isBulkUpdating} onClick={requestBulkRoleChange}>
              Change role
            </Button>
          </span>
        </Tooltip>
      </Toolbar>
    </Box>
  );

  const toolbarArea = (
    <Box sx={{ flex: '1 1 100%', minWidth: 0, display: 'grid', gap: 1.5 }}>
      {toolbar}
      {selectionRow}
    </Box>
  );

  const renderRow = (profile: Profile) => {
    const name = displayName(profile);
    const isSelf = Boolean(user && profile.id === user.id);
    const role = profile.role ?? 'employee';
    const selected = selectedIds.has(profile.id);
    const lastSignIn = signInTime(profile);
    const linkedId = profile.employeeId || '';
    // Department rides on the user's second line; the manager is one hover away, so rows stay one line tall.
    const secondary = [profile.email, profile.departmentName].filter(Boolean).join(' · ');
    const orgDetail = [profile.departmentName, profile.managerName && `Manager: ${profile.managerName}`].filter(Boolean).join(' · ');

    return (
      <TableRow key={profile.id} hover selected={selected}>
        <TableCell padding="checkbox">
          <Checkbox checked={selected} onChange={() => toggleSelection(profile.id)} inputProps={{ 'aria-label': `Select ${name}` }} />
        </TableCell>
        <TableCell sx={{ maxWidth: 300 }}>
          <PersonCell
            avatarName={avatarBasis(profile)}
            name={
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, maxWidth: '100%' }}>
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {name}
                </Box>
                {isSelf && (
                  <StatusPill tone="neutral" noDot>
                    You
                  </StatusPill>
                )}
              </Box>
            }
            secondary={
              orgDetail ? (
                <Tooltip title={orgDetail} placement="bottom-start">
                  <Box component="span">{secondary}</Box>
                </Tooltip>
              ) : (
                secondary
              )
            }
          />
        </TableCell>
        <TableCell>
          <Select
            size="small"
            value={role}
            onChange={(event) => requestRoleChange(profile, event.target.value as AuthRole)}
            inputProps={{ 'aria-label': `Role for ${name}` }}
            renderValue={(value) => roleLabel(value as AuthRole)}
            sx={{ ...quietFieldSx, ml: '-12px', minWidth: 140 }}
          >
            {roleMenuItems}
          </Select>
        </TableCell>
        <TableCell sx={{ minWidth: 240 }}>
          <LinkedEmployeePicker
            personName={name}
            linkedId={linkedId}
            fallbackName={profile.employeeName}
            options={employeeOptions}
            optionById={employeeOptionById}
            disabled={linkingId === profile.id}
            onChange={(employeeId) => handleEmployeeLink(profile, employeeId)}
          />
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
          {lastSignIn !== null ? (
            <Tooltip title={formatDateTime(profile.lastSignInAt)}>
              <Box component="span">{formatRelative(profile.lastSignInAt)}</Box>
            </Tooltip>
          ) : (
            <StatusPill tone="neutral">Never signed in</StatusPill>
          )}
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Reset password">
              <IconButton size="small" aria-label={`Reset password for ${profile.email}`} onClick={() => openResetDialog(profile)}>
                <KeyRound size={18} />
              </IconButton>
            </Tooltip>
            {canDeleteUsers && (
              <Tooltip title={isSelf ? 'You can’t delete your own account' : 'Delete user'}>
                <span>
                  <IconButton
                    size="small"
                    aria-label={`Delete user ${profile.email}`}
                    disabled={!user || isSelf || isDeleting}
                    onClick={() => openDeleteDialog(profile)}
                    sx={{ '&:hover': { color: sbg.status.danger.text, bgcolor: sbg.status.danger.bg } }}
                  >
                    <Trash2 size={18} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  let body: React.ReactNode;
  if (isLoading) {
    body = <TableSkeletonRows columns={COLUMN_COUNT} />;
  } else if (error) {
    body = (
      <TableRow sx={{ '&&:hover': { bgcolor: 'transparent' } }}>
        <TableCell colSpan={COLUMN_COUNT} sx={{ borderBottom: 0 }}>
          <ErrorState
            compact
            title="Couldn’t load user accounts"
            body="The server didn’t respond. Try again in a minute; nothing on this page was changed."
            onRetry={() => refetch?.()}
          />
        </TableCell>
      </TableRow>
    );
  } else if (total === 0) {
    body = (
      <TableEmptyRow
        colSpan={COLUMN_COUNT}
        title="No user accounts yet"
        body="Accounts appear here once you add them. Each one can sign in to Attendance."
        action={
          <Button variant="outlined" startIcon={<UserPlus />} onClick={openCreate}>
            Add the first user
          </Button>
        }
      />
    );
  } else if (sortedProfiles.length === 0) {
    body = (
      <TableEmptyRow
        colSpan={COLUMN_COUNT}
        title="No users match these filters"
        body={filterSummary ? `Nothing matches ${filterSummary}.` : undefined}
        action={
          <Button variant="outlined" onClick={clearFilters}>
            Clear filters
          </Button>
        }
      />
    );
  } else {
    body = pageRows.map(renderRow);
  }

  const showFooter = !isLoading && !error && sortedProfiles.length > 0;

  return (
    <Box sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
      <KpiGrid sx={{ mb: 0 }}>
        <KpiTile size="md" label="Accounts" value={kpiValue(total)} caption="Logins for Attendance" loading={isLoading} />
        <KpiTile
          size="md"
          label="Linked to employees"
          value={kpiValue(stats.linked)}
          caption={error || isLoading ? undefined : `${formatNumber(total - stats.linked)} not linked`}
          info="A linked account connects a login to its employee record."
          loading={isLoading}
        />
        <KpiTile size="md" label="Admins" value={kpiValue(stats.admins)} caption="Full system control" loading={isLoading} />
        <KpiTile
          size="md"
          label="Signed in recently"
          value={kpiValue(stats.recent)}
          caption={
            error || isLoading
              ? undefined
              : total
                ? `Last 30 days · ${formatPercent((stats.recent / total) * 100)} of accounts`
                : 'No accounts yet'
          }
          loading={isLoading}
        />
      </KpiGrid>

      <TableCard
        aria-label="User accounts"
        toolbar={toolbarArea}
        footer={
          showFooter ? (
            <TablePagination
              component="div"
              count={sortedProfiles.length}
              page={safePage}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              onPageChange={(_, next) => setPage(next)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value));
                setPage(0);
              }}
              labelRowsPerPage="Rows per page"
              labelDisplayedRows={({ from, to, count }) => `${formatNumber(from)}–${formatNumber(to)} of ${formatNumber(count)}`}
            />
          ) : undefined
        }
      >
        <Table sx={{ minWidth: 920 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={pageRows.length > 0 && pageSelectedCount === pageRows.length}
                  indeterminate={pageSelectedCount > 0 && pageSelectedCount < pageRows.length}
                  disabled={isLoading || Boolean(error) || pageRows.length === 0}
                  onChange={togglePageSelection}
                  inputProps={{ 'aria-label': 'Select all users on this page' }}
                />
              </TableCell>
              {sortableHeader('name', 'User')}
              {sortableHeader('role', 'Role')}
              <TableCell>Linked employee</TableCell>
              {sortableHeader('lastSignIn', 'Last sign-in', 'right')}
              <TableCell align="right">
                <span className="sbg-sr-only">Actions</span>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{body}</TableBody>
        </Table>
      </TableCard>

      <CreateUserSheet
        open={createOpen}
        form={createForm}
        onChange={setCreateForm}
        employees={employees}
        pending={isCreating}
        error={createError}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreateUser}
      />

      <RoleChangeDialog
        request={roleChange}
        currentUserId={user?.id}
        actorRole={actualRole}
        pending={roleChangePending || isBulkUpdating}
        error={roleChangeError}
        onCancel={() => {
          if (!roleChangePending) setRoleChange(null);
        }}
        onConfirm={handleRoleChange}
      />

      <ResetPasswordDialog
        target={resetTarget}
        password={resetPassword}
        onPasswordChange={setResetPassword}
        pending={isResetting}
        error={resetError}
        onCancel={closeResetDialog}
        onConfirm={handleResetPassword}
      />

      <DeleteUserDialog
        target={userToDelete}
        pending={isDeleting}
        canConfirm={canDeleteUsers && Boolean(user) && userToDelete?.id !== user?.id}
        error={deleteError}
        onCancel={closeDeleteDialog}
        onConfirm={handleDeleteUser}
      />

      {notice && (
        <Snackbar
          key={notice.id}
          open={noticeOpen}
          autoHideDuration={notice.severity === 'error' ? null : NOTICE_AUTO_HIDE_MS}
          onClose={(_, reason) => {
            if (reason !== 'clickaway') setNoticeOpen(false);
          }}
        >
          <Alert
            severity={notice.severity}
            role={notice.severity === 'error' ? 'alert' : 'status'}
            onClose={() => setNoticeOpen(false)}
          >
            {notice.text}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default UserAccess;
